import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import SafeIcon from '../../components/common/SafeIcon';
import ResourceContentFields, { FieldLabel, TextArea, TextInput } from '../../components/resources/ResourceContentFields';
import { useAuth } from '../../context/AuthContext';
import { COMPLIANCE_LABEL, SUGGESTED_TRAINING_TAGS } from '../../constants/training';
import { RESOURCE_AUDIENCES } from '../../constants/resources';
import {
  detectFileFormat,
  fetchDistinctResourceCategories,
  publishResource,
} from '../../services/resources';

export default function ResourceUploadPage() {
  const navigate = useNavigate();
  const { canUploadResources, loading: authLoading } = useAuth();

  const [categoryOptions, setCategoryOptions] = useState(SUGGESTED_TRAINING_TAGS);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [audience, setAudience] = useState('cliente');
  const [contentType, setContentType] = useState('file');
  const [file, setFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState('');
  const [linkKind, setLinkKind] = useState('google_docs');
  const [versionNote, setVersionNote] = useState('');
  const [compliance, setCompliance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDistinctResourceCategories()
      .then((fromDb) => {
        const merged = new Set([...SUGGESTED_TRAINING_TAGS, ...fromDb]);
        setCategoryOptions([...merged].sort((a, b) => a.localeCompare(b, 'es')));
      })
      .catch(console.error);
  }, []);

  const allCategories = useMemo(() => categoryOptions, [categoryOptions]);

  const resolvedCategory = category || categoryInput.trim();

  const validate = () => {
    if (!title.trim()) return 'El título es obligatorio';
    if (!resolvedCategory) return 'Selecciona o escribe una categoría';
    if (!audience) return 'Selecciona la audiencia';
    if (contentType === 'file' && !file) return 'Selecciona un archivo';
    if (contentType === 'link' && !externalUrl.trim()) return 'Ingresa la URL del enlace';
    if (!compliance) return 'Debes confirmar el cumplimiento de políticas';
    return null;
  };

  const buildContentPayload = () => {
    if (contentType === 'file') {
      const fileFormat = detectFileFormat(file);
      if (!fileFormat) {
        throw new Error('Formato no permitido. Usa PDF, DOCX o Markdown.');
      }

      return {
        type: 'file',
        file,
        fileFormat,
      };
    }

    return {
      type: 'link',
      externalUrl: externalUrl.trim(),
      linkKind,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setUploadPhase(contentType === 'file' ? 'Subiendo archivo…' : 'Guardando recurso…');

    try {
      const content = buildContentPayload();

      setUploadPhase('Publicando recurso…');
      const resourceId = await publishResource({
        title: title.trim(),
        description: description.trim(),
        category: resolvedCategory,
        audience,
        complianceConfirmed: compliance,
        content,
        versionNote,
      });

      navigate(`/app/recursos/${resourceId}`, {
        replace: true,
        state: { uploadSuccess: true },
      });
    } catch (err) {
      setError(err.message || 'No se pudo publicar el recurso');
    } finally {
      setSubmitting(false);
      setUploadPhase('');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
        Cargando…
      </div>
    );
  }

  if (!canUploadResources) {
    return <Navigate to="/app/recursos" replace />;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 pb-28">
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/app/recursos"
          className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] hover:bg-[var(--border-subtle)] transition-colors"
        >
          <SafeIcon name="ArrowLeft" className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[var(--text-main)] uppercase tracking-tight italic">
            Subir recurso
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            El recurso queda visible de inmediato para todos los miembros.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <section className="space-y-4">
          <div>
            <FieldLabel required>Título</FieldLabel>
            <TextInput value={title} onChange={(e) => setTitle(e.target.value)} disabled={submitting} required />
          </div>
          <div>
            <FieldLabel>Descripción</FieldLabel>
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              placeholder="Opcional — contexto para quien use este material"
            />
          </div>
        </section>

        <section className="space-y-4">
          <div>
            <FieldLabel required>Categoría</FieldLabel>
            <select
              value={category}
              onChange={(e) => { setCategory(e.target.value); setCategoryInput(''); }}
              disabled={submitting}
              className="w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-green mb-3"
            >
              <option value="">Seleccionar categoría…</option>
              {allCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <TextInput
              value={categoryInput}
              onChange={(e) => { setCategoryInput(e.target.value); setCategory(''); }}
              disabled={submitting}
              placeholder="O escribe una categoría nueva"
            />
          </div>

          <div>
            <FieldLabel required>Audiencia</FieldLabel>
            <div className="flex gap-2 p-1 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-full w-fit">
              {RESOURCE_AUDIENCES.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={submitting}
                  onClick={() => setAudience(opt.value)}
                  className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
                    audience === opt.value
                      ? 'gfi-gradient-bg text-brand-navy shadow-md'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <ResourceContentFields
          contentType={contentType}
          onContentTypeChange={setContentType}
          file={file}
          onFileChange={setFile}
          externalUrl={externalUrl}
          onExternalUrlChange={setExternalUrl}
          linkKind={linkKind}
          onLinkKindChange={setLinkKind}
          versionNote={versionNote}
          onVersionNoteChange={setVersionNote}
          versionLabel=""
          onVersionLabelChange={() => {}}
          showVersionFields
          submitting={submitting}
        />

        <section className="p-4 rounded-xl border border-brand-cyan/30 bg-brand-cyan/5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={compliance}
              onChange={(e) => setCompliance(e.target.checked)}
              disabled={submitting}
              required
              className="mt-1 h-4 w-4 rounded border-[var(--border-subtle)]"
            />
            <span className="text-sm text-[var(--text-main)] leading-relaxed">{COMPLIANCE_LABEL}</span>
          </label>
        </section>

        <button
          type="submit"
          disabled={submitting || !compliance}
          className="w-full md:w-auto flex items-center justify-center gap-3 gfi-gradient-bg text-brand-navy px-10 py-4 rounded-full font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 shadow-xl"
        >
          {submitting ? (
            <>
              <SafeIcon name="Loader" className="w-5 h-5 animate-spin" />
              {uploadPhase || 'Publicando…'}
            </>
          ) : (
            <>
              <SafeIcon name="Upload" className="w-5 h-5" />
              Publicar recurso
            </>
          )}
        </button>
      </form>
    </div>
  );
}
